import { appBrandSchema } from "./types";

import type { CustomerBranding } from "../types";
import type { AppBrand } from "./types";

/** Named rather than boolean, so a save that cannot be applied can say why.
    The resolver and the editor must agree on this, or one reports success while
    the other refuses to resolve — hence the logo's signed URL rather than the
    logo: signing is allowed to fail, and an asset without one is exactly what
    `resolveAppBrand` throws on. */
export const missingForApply = (branding: CustomerBranding): string[] =>
  (
    [
      [branding.config.web?.primary_color, "brand colour"],
      [branding.config.web?.logo?.signedUrl, "logo"],
      [branding.config.web?.app_name, "app name"],
      [branding.config.web?.tab_title, "browser tab title"],
    ] as const
  )
    .filter(([value]) => !value)
    .map(([, label]) => label);

export const isAppBrandingComplete = (branding: CustomerBranding): boolean =>
  missingForApply(branding).length === 0;

/** Assets are the signed URLs the profile already carries. They render through
    `<img src>` and the favicon `<link>`, so nothing reads their bytes and no
    CORS grant on the uploads bucket is needed. */
export const resolveAppBrand = (branding: CustomerBranding): AppBrand => {
  const logo = branding.config.web?.logo?.signedUrl;
  if (!logo) {
    throw new Error(`${branding.name} has no logo to resolve`);
  }

  return appBrandSchema.parse({
    id: branding.id,
    name: branding.name,
    appName: branding.config.web?.app_name,
    tabTitle: branding.config.web?.tab_title,
    primary: branding.config.web?.primary_color,
    gradient: branding.config.web?.sidebar_gradient,
    logo,
    favicon: branding.config.web?.favicon?.signedUrl ?? logo,
  });
};
